/**
 * usePortfolioSync Hook
 * Hook for managing portfolio sync status and history
 * 
 * ⚠️ CRITICAL: Per ADR-FE-001, this hook only fetches and displays pre-calculated data.
 * All sync operations are handled by backend Portfolio Service.
 */

import { useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { fetchSyncStatus, fetchSyncHistory, syncPortfolio } from '../../../store/slices/portfolioSlice';
import { PortfolioSyncStatus, PortfolioSyncHistory } from '../../../types/portfolio';

export interface UsePortfolioSyncOptions {
  brokerAccountId: string;
  limit?: number;
  autoRefresh?: boolean;
}

export interface UsePortfolioSyncResult {
  syncStatus: PortfolioSyncStatus[];
  syncHistory: PortfolioSyncHistory[];
  loading: boolean;
  error: string | null;
  syncInProgress: boolean;
  refreshStatus: () => Promise<void>;
  refreshHistory: () => Promise<void>;
  triggerSync: (scope?: 'holdings_only' | 'transactions_only' | 'both') => Promise<void>;
}

export function usePortfolioSync(
  options: UsePortfolioSyncOptions
): UsePortfolioSyncResult {
  const dispatch = useAppDispatch();
  const syncStatus = useAppSelector((state) => state.portfolio.syncStatus);
  const syncHistory = useAppSelector((state) => state.portfolio.syncHistory);
  const syncLoading = useAppSelector((state) => state.portfolio.syncLoading);
  const syncInProgressMap = useAppSelector((state) => state.portfolio.syncInProgress);
  const error = useAppSelector((state) => state.portfolio.error);

  const syncInProgress = syncInProgressMap[options.brokerAccountId] || false;

  const refreshStatus = useCallback(async () => {
    try {
      await dispatch(fetchSyncStatus({ brokerAccountId: options.brokerAccountId, limit: options.limit })).unwrap();
    } catch (err) {
      // Error handled by Redux slice
      // eslint-disable-next-line no-console
      console.error('Failed to refresh sync status:', err);
    }
  }, [dispatch, options.brokerAccountId, options.limit]);

  const refreshHistory = useCallback(async () => {
    try {
      await dispatch(fetchSyncHistory({ brokerAccountId: options.brokerAccountId, limit: options.limit })).unwrap();
    } catch (err) {
      // Error handled by Redux slice
      // eslint-disable-next-line no-console
      console.error('Failed to refresh sync history:', err);
    }
  }, [dispatch, options.brokerAccountId, options.limit]);

  const triggerSync = useCallback(async (scope: 'holdings_only' | 'transactions_only' | 'both' = 'both') => {
    try {
      await dispatch(syncPortfolio({ brokerAccountId: options.brokerAccountId, scope })).unwrap();
      // Refresh status after sync
      await refreshStatus();
    } catch (err) {
      // Error handled by Redux slice
      // eslint-disable-next-line no-console
      console.error('Failed to trigger sync:', err);
    }
  }, [dispatch, options.brokerAccountId, refreshStatus]);

  useEffect(() => {
    if (options.autoRefresh !== false) {
      refreshStatus();
      refreshHistory();
    }
  }, [refreshStatus, refreshHistory, options.autoRefresh]);

  return {
    syncStatus,
    syncHistory,
    loading: syncLoading,
    error,
    syncInProgress,
    refreshStatus,
    refreshHistory,
    triggerSync,
  };
}

